//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useDebounce } from 'use-debounce';

import { Alert } from '@components/atoms/alert/Alert';
import { DefaultIconButton } from '@components/atoms/button/Button';
import { HelpIconWithMargin } from '@components/atoms/help-icon/HelpIcon';
import { BodyBoldText, BodyText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { Upload } from '@components/molecules/upload/Upload';
import Cuid from '@models/Cuid';
import FileId from '@models/FileId';
import { Mixins } from '@theme/mixins';
import {
  DEFAULT_FRAMES_PER_SECOND,
  UIACoverImageTrackingIdPrefix,
  UIATrackingIds,
  UIA_CUSTOM_COVER_DESCRIPTION,
  UIA_CUSTOM_COVER_DRAG_DESCRIPTION,
  UIA_CUSTOM_COVER_DRAG_UPLOAD_DESCRIPTION,
  UIA_CUSTOM_COVER_SELECTION_PREVIEW_ASPECT_RATIO_PORTRAIT,
  UIA_CUSTOM_COVER_SELECTION_PREVIEW_ASPECT_RATIO_SQUARE,
  UIA_CUSTOM_COVER_SELECTION_PREVIEW_MAX_HEIGHT,
  UIA_CUSTOM_COVER_UPLOAD_DESCRIPTION,
  UIA_CUSTOM_COVER_UPLOAD_HELP_TEXT,
  UIA_CUSTOM_COVER_UPLOAD_LIMIT,
  UIA_CUSTOM_COVER_UPLOAD_SIZE_ERROR,
  UIA_SLIDER_DEBOUNCE_DELAY_MS,
  VIDEO_OFFSET_CONVERSION_FACTOR,
} from '@uia/constants/uiaConstants';
import { UIACustomCoverSelectionSlider } from '@uia/custom-cover-image/UIACustomCoverSelectionSlider';
import {
  useSetCustomThumbnail,
  useUploadCoverImage,
} from '@uia/custom-cover-image/hooks/uiaCustomCoverImageQueries';
import {
  isDefined,
  isNotNull,
  isTruthy,
  isUndefined,
} from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { getMedsSizedImageUrl } from '@utils/meds';
import { SUPPORTED_IMAGE_MIME_TYPES } from '@utils/mimeTypes';
import { RequestError } from '@utils/sessionFetch';

interface IPreviewDisplayInfo {
  aspectRatio: number;
  title?: string;
  objectFit?: string;
}

interface Props {
  coverImageFileId?: FileId;
  durationMillis?: number;
  framesPerSecond?: number;
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  url?: string;
  videoThumbnailOffsetSeconds?: number;
  hasCustomThumbnailOffset?: boolean;
  fileId?: FileId;
  cuid: Cuid;
  isBrandOwned?: boolean;
}

export const UIACustomCoverImageSelectionModal = ({
  coverImageFileId,
  cuid,
  durationMillis,
  framesPerSecond = DEFAULT_FRAMES_PER_SECOND,
  hasCustomThumbnailOffset = false,
  isBrandOwned = true,
  isModalOpen,
  setIsModalOpen,
  url,
  videoThumbnailOffsetSeconds = 0,
}: Props) => {
  const { isPending: isSaving, mutateAsync: setCustomThumbnailAsync } =
    useSetCustomThumbnail();
  const theme = useTheme();
  // Constant for floating point comparison
  const EPSILON = 0.01;

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [localCoverImageFileId, setLocalCoverImageFileId] = useState<
    FileId | undefined
  >(coverImageFileId);

  const [initialCoverImageFileId, setInitialCoverImageFileId] = useState<
    FileId | undefined
  >(coverImageFileId);
  const [initialVideoOffset, setInitialVideoOffset] = useState<number>(
    videoThumbnailOffsetSeconds || 0,
  );
  const [currentVideoOffset, setCurrentVideoOffset] = useState<number>(
    videoThumbnailOffsetSeconds || 0,
  );

  const [isUsingCustomOffset, setIsUsingCustomOffset] = useState<boolean>(
    hasCustomThumbnailOffset,
  );

  const [wasCustomImageRemoved, setWasCustomImageRemoved] =
    useState<boolean>(false);

  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const { mutateAsync: uploadCoverImageAsync } = useUploadCoverImage();

  const [actualVideoDuration, setActualVideoDuration] = useState<
    number | undefined
  >(
    durationMillis !== undefined
      ? durationMillis / VIDEO_OFFSET_CONVERSION_FACTOR
      : undefined,
  );

  const [videosLoaded, setVideosLoaded] = useState<boolean>(
    durationMillis !== undefined,
  );

  const coverImageUrl = isDefined(localCoverImageFileId)
    ? getMedsSizedImageUrl(localCoverImageFileId, 'l')
    : undefined;

  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  useEffect(() => {
    return () => {
      videoRefs.current = [];
    };
  }, []);

  const validateCoverImageUpload = (file: File) => {
    if (file.size > UIA_CUSTOM_COVER_UPLOAD_LIMIT) {
      return UIA_CUSTOM_COVER_UPLOAD_SIZE_ERROR;
    }
    return undefined;
  };

  const [pendingSliderValue, setPendingSliderValue] = useState<number | null>(
    null,
  );

  const [debouncedSliderValue] = useDebounce(
    pendingSliderValue,
    UIA_SLIDER_DEBOUNCE_DELAY_MS,
  );

  useEffect(() => {
    if (debouncedSliderValue !== null) {
      setCurrentVideoOffset(debouncedSliderValue);
    }
  }, [debouncedSliderValue]);

  useEffect(() => {
    if (!isModalOpen || !url) {
      return;
    }

    setCurrentVideoOffset(videoThumbnailOffsetSeconds);

    videoRefs.current.forEach((videoRef) => {
      if (!isTruthy(videoRef)) {
        return;
      }

      if (videoRef.duration) {
        setActualVideoDuration(videoRef.duration);
      }

      const offsetSeconds = videoThumbnailOffsetSeconds;

      const videoDuration = videoRef.duration;
      if (!videoDuration || offsetSeconds <= videoDuration) {
        videoRef.currentTime = offsetSeconds;
      }
    });
  }, [isModalOpen, videoThumbnailOffsetSeconds, url]);

  const clearUploadInput = useCallback(() => {
    if (uploadInputRef.current?.value) {
      uploadInputRef.current.value = '';
    }
  }, []);

  const initializeModalState = useCallback(() => {
    const offset = videoThumbnailOffsetSeconds || 0;

    setInitialCoverImageFileId(coverImageFileId);
    setInitialVideoOffset(offset);

    // Explicitly set isUsingCustomOffset based on hasCustomThumbnailOffset
    // This is critical for determining what to show in the preview
    setIsUsingCustomOffset(hasCustomThumbnailOffset);

    setCurrentVideoOffset(offset);
    setLocalCoverImageFileId(coverImageFileId);

    setErrorMessage(undefined);
    setWasCustomImageRemoved(false);
    clearUploadInput();
  }, [
    coverImageFileId,
    videoThumbnailOffsetSeconds,
    hasCustomThumbnailOffset,
    clearUploadInput,
  ]);

  const resetModalState = useCallback(() => {
    setLocalCoverImageFileId(initialCoverImageFileId);
    setCurrentVideoOffset(initialVideoOffset);
    setIsUsingCustomOffset(hasCustomThumbnailOffset);
    setWasCustomImageRemoved(false);

    setErrorMessage(undefined);

    clearUploadInput();

    videoRefs.current.forEach((videoRef) => {
      if (isTruthy(videoRef)) {
        videoRef.currentTime = initialVideoOffset;
      }
    });
  }, [
    initialCoverImageFileId,
    initialVideoOffset,
    hasCustomThumbnailOffset,
    clearUploadInput,
  ]);

  useEffect(() => {
    if (isModalOpen) {
      initializeModalState();
    }
  }, [isModalOpen, initializeModalState]);

  const getDescription = useCallback((): string => {
    if (url && isBrandOwned) {
      return UIA_CUSTOM_COVER_DRAG_UPLOAD_DESCRIPTION;
    } else if (url) {
      return UIA_CUSTOM_COVER_DRAG_DESCRIPTION;
    } else if (isBrandOwned) {
      return UIA_CUSTOM_COVER_UPLOAD_DESCRIPTION;
    } else {
      return UIA_CUSTOM_COVER_DESCRIPTION;
    }
  }, [url, isBrandOwned]);

  const MIN_OFFSET_MARGIN_SEC = 0.2;

  const clampOffsetToVideoDuration = (
    offset: number,
    duration?: number,
    margin: number = MIN_OFFSET_MARGIN_SEC,
  ): number => {
    if (typeof duration !== 'number') {
      return offset;
    }
    const maxAllowed = Math.max(0, duration - margin);
    return Math.min(offset, maxAllowed);
  };

  const handleSliderChange = (newVal: unknown) => {
    if (typeof newVal !== 'number') {
      return;
    }

    const clampedValue = clampOffsetToVideoDuration(
      newVal,
      actualVideoDuration,
    );

    if (Math.abs(clampedValue - currentVideoOffset) > EPSILON) {
      setPendingSliderValue(clampedValue);
    }

    videoRefs.current.forEach((videoRef) => {
      if (!isNotNull(videoRef)) {
        return;
      }
      const videoDuration = videoRef.duration;
      if (!videoDuration || clampedValue <= videoDuration) {
        videoRef.currentTime = clampedValue;
      }
    });
  };

  const previewDisplayInfo: IPreviewDisplayInfo[] = [
    {
      aspectRatio: UIA_CUSTOM_COVER_SELECTION_PREVIEW_ASPECT_RATIO_PORTRAIT,
      title: 'Portrait (9:16)',
    },
    {
      aspectRatio: UIA_CUSTOM_COVER_SELECTION_PREVIEW_ASPECT_RATIO_SQUARE,
      title: 'Square (1:1)',
    },
  ];

  const previewCover = (
    previewDisplayInfo: IPreviewDisplayInfo,
    previewIndex: number,
  ) => {
    if (
      isDefined(localCoverImageFileId) &&
      isDefined(coverImageUrl) &&
      !isUsingCustomOffset
    ) {
      return (
        <CoverImagePreview
          $aspectRatio={previewDisplayInfo.aspectRatio}
          $objectFit={previewDisplayInfo.objectFit}
          alt={previewDisplayInfo.title}
          src={coverImageUrl}
        />
      );
    }

    if (isUsingCustomOffset && url) {
      return (
        <Video
          ref={(videoRef) => {
            if (isTruthy(videoRef)) {
              videoRefs.current[previewIndex] = videoRef;
            }
          }}
          $aspectRatio={previewDisplayInfo.aspectRatio}
          controls={false}
          muted={true}
          onChange={handleSliderChange}
          onLoadedMetadata={() => {
            const videoRef = videoRefs.current[previewIndex];
            if (!isTruthy(videoRef)) {
              return;
            }

            if (videoRef.duration) {
              setActualVideoDuration(videoRef.duration);
              setVideosLoaded(true);
            }

            const offsetSeconds = videoThumbnailOffsetSeconds;

            const videoDuration = videoRef.duration;
            if (!videoDuration || offsetSeconds <= videoDuration) {
              videoRef.currentTime = offsetSeconds;
            }
          }}
          src={url}
        />
      );
    }

    return (
      <Video
        ref={(videoRef) => {
          if (isTruthy(videoRef)) {
            videoRefs.current[previewIndex] = videoRef;
          }
        }}
        $aspectRatio={previewDisplayInfo.aspectRatio}
        controls={false}
        muted={true}
        onChange={handleSliderChange}
        onLoadedMetadata={() => {
          const videoRef = videoRefs.current[previewIndex];
          if (!isTruthy(videoRef)) {
            return;
          }

          if (videoRef.duration) {
            setActualVideoDuration(videoRef.duration);
            setVideosLoaded(true);
          }

          const offsetSeconds = videoThumbnailOffsetSeconds;

          const videoDuration = videoRef.duration;
          if (!videoDuration || offsetSeconds <= videoDuration) {
            videoRef.currentTime = offsetSeconds;
          }
        }}
        src={url}
      />
    );
  };

  const hasChangesToSave = (): boolean => {
    if (
      (isDefined(localCoverImageFileId) &&
        isUndefined(initialCoverImageFileId)) ||
      (isUndefined(localCoverImageFileId) &&
        isDefined(initialCoverImageFileId) &&
        url) ||
      (isDefined(localCoverImageFileId) &&
        isDefined(initialCoverImageFileId) &&
        localCoverImageFileId.toString() !== initialCoverImageFileId.toString())
    ) {
      return true;
    }

    if (wasCustomImageRemoved && isDefined(initialCoverImageFileId) && url) {
      return true;
    }

    if (
      url &&
      (isUndefined(localCoverImageFileId) || isUsingCustomOffset) &&
      Math.abs(currentVideoOffset - initialVideoOffset) > EPSILON
    ) {
      return true;
    }

    return false;
  };

  const onUploadFile = async (fileList: FileList | null) => {
    const file = fileList?.[0];

    if (isUndefined(file)) {
      return;
    }

    setErrorMessage(undefined);

    const err = validateCoverImageUpload(file);

    if (isDefined(err)) {
      setErrorMessage(err);
      return;
    }

    try {
      const response = await uploadCoverImageAsync(file);

      setIsUsingCustomOffset(false);
      setLocalCoverImageFileId(response.fileId);
    } catch (error) {
      if (error instanceof RequestError) {
        const errorMessage = getRequestErrorMessage(error);
        setErrorMessage(errorMessage);
      } else {
        setErrorMessage(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        );
      }
    }
  };

  return (
    <Modal
      closeModal={() => {
        resetModalState();
        setIsModalOpen(false);
      }}
      contents={
        <Contents>
          <DescriptionWrapper>
            {isDefined(errorMessage) && (
              <StyledAlert type='error'>{errorMessage}</StyledAlert>
            )}
            <BodyText>{getDescription()}</BodyText>
          </DescriptionWrapper>
          {(url || isDefined(localCoverImageFileId)) && (
            <MediaWrapper>
              {previewDisplayInfo.map((preview, index) => (
                <PreviewCover key={`${index}-${preview.title}`}>
                  {isTruthy(preview.title) && (
                    <BodyBoldText>{preview.title}</BodyBoldText>
                  )}
                  {previewCover(preview, index)}
                </PreviewCover>
              ))}
            </MediaWrapper>
          )}
          {/* Show slider when:
              1. url is available (there's a video to seek)
              2. Either:
                 a. No custom cover image is set yet (localCoverImageFileId is undefined)
                 b. Custom cover image was set from video frame (hasCustomThumbnailOffset is true)
          */}
          {/* Only show slider when we have a valid duration (either from metadata or loaded video) */}
          {url &&
            (isUndefined(localCoverImageFileId) || isUsingCustomOffset) &&
            videosLoaded && (
              <SliderWrapper>
                <UIACustomCoverSelectionSlider
                  defaultValue={videoThumbnailOffsetSeconds}
                  framesPerSecond={framesPerSecond}
                  maxValue={
                    actualVideoDuration
                      ? clampOffsetToVideoDuration(
                          actualVideoDuration,
                          actualVideoDuration,
                        )
                      : 0
                  }
                  onChange={handleSliderChange}
                  trackingIdPrefix={UIACoverImageTrackingIdPrefix}
                />
              </SliderWrapper>
            )}
          {isBrandOwned && (
            <CustomCoverImageSection>
              <Upload
                ref={uploadInputRef}
                acceptableFiles={SUPPORTED_IMAGE_MIME_TYPES}
                buttonText='Upload Cover Image'
                handleChange={onUploadFile}
                isDisabled={false}
                shouldAcceptMultipleFiles={false}
                trackingId={UIATrackingIds.COVER_IMAGE_UPLOAD_BUTTON}
              />
              {(isUndefined(localCoverImageFileId) || isUsingCustomOffset) && (
                <HelpIconWithMargin
                  helpText={UIA_CUSTOM_COVER_UPLOAD_HELP_TEXT}
                  placement='right'
                />
              )}
              {/* Only show the CustomCoverImagePreview with remove button for uploaded images, not for images set through video frames */}
              {isDefined(localCoverImageFileId) &&
                isDefined(coverImageUrl) &&
                !isUsingCustomOffset && (
                  <CustomCoverImagePreview>
                    <CustomCoverImage
                      alt='Uploaded cover image preview'
                      src={coverImageUrl}
                    />
                    <SmallTooltip
                      appendTarget='body'
                      content='Remove your cover image.'
                      offset={{ distance: parseInt(theme.spacings[5], 10) }}
                      placement='top'
                      target={
                        <StyledCloseButton
                          aria-label='Remove your cover image'
                          iconColor={theme.colors.black}
                          iconName='close'
                          onClick={() => {
                            // If we're removing a previously saved image (initialCoverImageFileId exists),
                            // mark it as removed to enable the confirm button
                            if (isDefined(initialCoverImageFileId)) {
                              setWasCustomImageRemoved(true);
                            }

                            setLocalCoverImageFileId(undefined);

                            // Only set isUsingCustomOffset to true when removing an uploaded image
                            // if there's a video URL available
                            if (url) {
                              setIsUsingCustomOffset(true);

                              // Reset the video offset to 0 if there's no custom offset in the metadata
                              // Otherwise, use the offset from props
                              if (!hasCustomThumbnailOffset) {
                                setCurrentVideoOffset(0);
                                setInitialVideoOffset(0);
                              } else {
                                setCurrentVideoOffset(
                                  videoThumbnailOffsetSeconds || 0,
                                );
                                setInitialVideoOffset(
                                  videoThumbnailOffsetSeconds || 0,
                                );
                              }
                            }

                            // need to clear out the input's value (must be a string) or else the Upload button's handleChange will
                            // not detect a change in value if the same image is uploaded again
                            if (uploadInputRef.current?.value) {
                              uploadInputRef.current.value = '';
                            }
                          }}
                        />
                      }
                    />
                  </CustomCoverImagePreview>
                )}
            </CustomCoverImageSection>
          )}
        </Contents>
      }
      isModalOpen={isModalOpen}
      primaryButtonInfo={{
        buttonContent: isSaving ? 'Saving' : 'Confirm',
        isDisabled: !hasChangesToSave() || isSaving,
        onClick: () => {
          if (hasChangesToSave()) {
            const params: {
              cuid: Cuid;
              thumbnailFileId?: FileId;
              thumbnailOffsetMilliseconds?: number;
            } = {
              cuid: cuid,
            };

            if (isDefined(localCoverImageFileId) && !isUsingCustomOffset) {
              params.thumbnailFileId = localCoverImageFileId;
              params.thumbnailOffsetMilliseconds = undefined;
            } else {
              const boundedOffset = clampOffsetToVideoDuration(
                currentVideoOffset,
                actualVideoDuration,
              );
              params.thumbnailOffsetMilliseconds = Math.round(
                boundedOffset * VIDEO_OFFSET_CONVERSION_FACTOR,
              );
              params.thumbnailFileId = undefined;
            }

            void setCustomThumbnailAsync(params, {
              onSuccess: () => {
                if (isUndefined(localCoverImageFileId)) {
                  setIsUsingCustomOffset(true);
                  setLocalCoverImageFileId(undefined);
                }
                setIsModalOpen(false);
              },
              onError: () => {
                setErrorMessage(
                  'An error occurred while saving. Please try again or contact Bazaarvoice Support.',
                );
              },
            });
          }
        },
        trackingId: `${UIATrackingIds.COVER_IMAGE_CONFIRM_BUTTON}`,
      }}
      title='Change Video Cover Image'
      width='wide'
      zIndexOverlayOverride={theme.zindex.modal} // to ensure the modal is displayed on top of the UIA overlay
    />
  );
};

const Contents = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: ${theme.wideMeasure};
  z-index: ${theme.zindex.popover};
  padding-left: ${theme.spacings[2]};
`,
);

const DescriptionWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const StyledAlert = styled(Alert)`
  width: 100%;
`;

const SliderWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  padding: 0 ${theme.spacings[3]} 0 ${theme.spacings[1]};
  width: 100%;
`,
);

const MediaWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  justify-content: flex-start;
  padding-top: ${theme.spacings[3]};
  gap: ${theme.spacings[2]};
`,
);

const PreviewCover = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spacings[2]};
`,
);

const ThumbnailPreviewStyles = (aspectRatio: number) => {
  const size =
    aspectRatio === UIA_CUSTOM_COVER_SELECTION_PREVIEW_ASPECT_RATIO_SQUARE
      ? 160
      : UIA_CUSTOM_COVER_SELECTION_PREVIEW_MAX_HEIGHT;
  return `
    height: ${size}px;
    max-height: ${size}px;
    object-fit: cover;
  `;
};

const Video = styled.video<{ $aspectRatio: number }>(
  ({ $aspectRatio }) => `
  ${ThumbnailPreviewStyles($aspectRatio)}
  aspect-ratio: ${$aspectRatio};
`,
);

const CoverImagePreview = styled.img<{
  $aspectRatio: number;
  $objectFit?: string;
}>(
  ({ $aspectRatio, $objectFit }) => `
  ${ThumbnailPreviewStyles($aspectRatio)}
  aspect-ratio: ${$aspectRatio};
  ${isDefined($objectFit) && `object-fit: ${$objectFit};`}
 
`,
);

const CustomCoverImageSection = styled.div(
  ({ theme }) => `
  align-self: flex-start;
  display: flex;
  align-items: center;
  margin-top: ${theme.spacings[3]};
`,
);

const CustomCoverImagePreview = styled.div(
  ({ theme }) => `
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: ${theme.spacings[2]};
  margin-right: ${theme.spacings[2]};
  width: ${theme.spacings[6]};
  aspect-ratio: 1;
  border: 1px solid ${theme.colors.gray2};
  `,
);

const CustomCoverImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const StyledCloseButton = styled(DefaultIconButton)(
  ({ theme }) => css`
    /* We have to directly select the button element because the Button component applies the
    className to the wrapping <div> instead of the <button> */
    && button {
      &:focus {
        ${Mixins.Focus}
        border-radius: 50%;
      }
    }
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(50%, -50%);
    margin-top: ${theme.spacings[1]};
    border-radius: 50%;

    height: calc(${theme.spacings[3]} + ${theme.spacings[2]});
    max-width: calc(${theme.spacings[3]} + ${theme.spacings[2]});
    min-width: calc(${theme.spacings[3]} + ${theme.spacings[2]});

    svg {
      height: calc(${theme.spacings[2]} + ${theme.spacings[1]});
      width: calc(${theme.spacings[2]} + ${theme.spacings[1]});
    }
  `,
);
